import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface BellIconProps {
  unreadCount: number;
  onClick: () => void;
  isOpen: boolean;
}

/**
 * Bell Icon Component with Unread Badge
 * Shows notification bell with unread count indicator
 */
export const BellIcon: React.FC<BellIconProps> = ({ unreadCount, onClick, isOpen }) => {
  const { theme } = useTheme();

  return (
    <button
      onClick={onClick}
      className={`relative p-1.5 md:p-2 rounded-lg transition-all border ${
        isOpen
          ? theme === 'light'
            ? 'bg-sky-50 border-sky-300 shadow-md'
            : 'bg-cyan-900/30 border-cyan-500/50 shadow-lg shadow-cyan-500/10'
          : theme === 'light'
          ? 'bg-white hover:bg-slate-50 border-slate-200'
          : 'bg-slate-900 hover:bg-slate-700 border-slate-600'
      }`}
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
    >
      <svg
        className={`w-6 h-6 transition-colors ${
          isOpen
            ? theme === 'light'
              ? 'text-sky-600'
              : 'text-cyan-400'
            : theme === 'light'
            ? 'text-slate-600'
            : 'text-slate-200'
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>

      {/* Unread Badge */}
      {unreadCount > 0 && (
        <span
          className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-xs font-bold shadow-lg ${
            theme === 'light'
              ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white'
              : 'bg-gradient-to-r from-rose-400 to-pink-400 text-white'
          }`}
          style={{
            animation: unreadCount > 0 ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
          }}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
};
